import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Plus, ShieldCheck, ArrowUpCircle } from 'lucide-react';
import { ticketingApi, type Contract } from '@/lib/ticketing-api';
import { inventoryApi } from '@/lib/inventory-api';
import { PageHeader, DataTable, type Column } from '@/components/ui/DataTable';
import { StatusPill, KpiStat } from '@/components/ui/primitives';
import { QuickFormModal } from '@/components/QuickFormModal';

const money = (v?: number, c?: string) => (v == null ? '—' : `${c === 'USD' ? '$' : ''}${v.toLocaleString()}`);
const daysTo = (d?: string | null) => (d ? Math.round((new Date(d).getTime() - Date.now()) / 86400000) : null);

const LEVEL_TONE = ['text-muted', 'text-muted', 'text-warning', 'text-warning', 'text-critical'];

export function ContractsPage() {
  const { t } = useTranslation('pages');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const { data: contracts, isLoading } = useQuery({ queryKey: ['contracts'], queryFn: ticketingApi.listContracts });
  const { data: vendors } = useQuery({ queryKey: ['vendors'], queryFn: ticketingApi.listVendors });
  const { data: esc } = useQuery({ queryKey: ['escalations'], queryFn: inventoryApi.escalations });

  const active = (contracts ?? []).filter((c) => c.status === 'active').length;
  const renewSoon = (contracts ?? []).filter((c) => { const d = daysTo(c.endDate); return d !== null && d <= 60; }).length;
  const totalValue = (contracts ?? []).reduce((s, c) => s + (c.totalValue ?? 0), 0);

  const columns: Column<Contract>[] = [
    { key: 'contractNumber', header: t('contracts.columns.contractNumber'), className: 'tk-num font-medium' },
    { key: 'name', header: t('contracts.columns.name') },
    { key: 'vendorName', header: t('contracts.columns.vendor'), render: (c) => <span className="text-muted">{c.vendorName}</span> },
    { key: 'type', header: t('contracts.columns.type'), render: (c) => <span className="text-muted capitalize">{c.type}</span> },
    { key: 'status', header: t('contracts.columns.status'), render: (c) => <StatusPill status={c.status} /> },
    { key: 'sla', header: t('contracts.columns.sla'), render: (c) => <span className="tk-num text-muted">{c.slaResponseH}h / {c.slaResolutionH}h</span> },
    { key: 'totalValue', header: t('contracts.columns.value'), align: 'right', render: (c) => <span className="tk-num">{money(c.totalValue, c.currency)}</span> },
    {
      key: 'endDate', header: t('contracts.columns.ends'), align: 'right',
      render: (c) => {
        const d = daysTo(c.endDate);
        const soon = d !== null && d <= 60;
        return (
          <span className={`tk-num ${soon ? 'text-warning' : 'text-muted'}`}>
            {c.endDate ? new Date(c.endDate).toLocaleDateString() : '—'}{soon ? ` · ${d}d` : ''}
          </span>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('contracts.title')}
        subtitle={t('contracts.subtitle')}
        action={<button className="tk-btn-primary" onClick={() => setAddOpen(true)}><Plus size={16} /> {t('contracts.actions.newContract')}</button>}
      />

      {addOpen && (
        <QuickFormModal
          title={t('contracts.modal.title')}
          submitLabel={t('contracts.modal.submit')}
          fields={[
            { key: 'name', label: t('contracts.modal.fields.name'), required: true, placeholder: t('contracts.modal.placeholders.name') },
            { key: 'contractNumber', label: t('contracts.modal.fields.contractNumber'), required: true, placeholder: `CTR-${new Date().getFullYear()}-0001` },
            { key: 'vendorId', label: t('contracts.modal.fields.vendor'), type: 'select', required: true,
              options: (vendors ?? []).map((v) => ({ value: v.id, label: v.name })) },
            { key: 'type', label: t('contracts.modal.fields.type'), type: 'select', defaultValue: 'maintenance', options: [
              { value: 'maintenance', label: t('contracts.type.maintenance') }, { value: 'service', label: t('contracts.type.service') },
              { value: 'support', label: t('contracts.type.support') }, { value: 'license', label: t('contracts.type.license') },
            ] },
            { key: 'status', label: t('contracts.modal.fields.status'), type: 'select', defaultValue: 'active', options: [
              { value: 'active', label: t('contracts.status.active') }, { value: 'draft', label: t('contracts.status.draft') }, { value: 'pending', label: t('contracts.status.pending') },
            ] },
          ]}
          onClose={() => setAddOpen(false)}
          onSubmit={async (v) => {
            await ticketingApi.createContract({ name: v.name, contractNumber: v.contractNumber, vendorId: v.vendorId, type: v.type || 'maintenance', status: v.status || 'active' });
            queryClient.invalidateQueries({ queryKey: ['contracts'] });
          }}
        />
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiStat label={t('contracts.kpi.active')} value={active} tone="ok" />
        <KpiStat label={t('contracts.kpi.renewingSoon')} value={renewSoon} tone={renewSoon ? 'warning' : 'ok'} />
        <KpiStat label={t('contracts.kpi.totalValue')} value={money(totalValue, 'USD')} tone="info" />
        <KpiStat label={t('contracts.kpi.contracts')} value={contracts?.length ?? 0} tone="info" />
      </div>

      {/* SLA tiers + escalation policy now live ON each contract (click a row). */}
      <div className="tk-card flex items-center gap-2 text-xs text-muted">
        <ShieldCheck size={14} className="text-primary" />
        {t('contracts.slaBanner.before')} <b className="text-fg/80">{t('contracts.slaBanner.emphasis')}</b> {t('contracts.slaBanner.after')}
      </div>

      <DataTable
        columns={columns}
        rows={contracts}
        loading={isLoading}
        emptyIcon={FileText}
        emptyTitle={t('contracts.empty.title')}
        emptyHint={t('contracts.empty.hint')}
        onRowClick={(c) => navigate(`/contracts/${c.id}`)}
      />
    </div>
  );
}
